import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "../lib/api";
import { getAll, STORES } from "../lib/db";
import {
    CheckCircle, WarningCircle, Flask, ArrowRight, ShieldCheck, Wrench,
} from "@phosphor-icons/react";

/* Structural model gaps from the June 2026 logic + calibration review.
 * All six are now shipped — see romEngine.js / rate_schedule_seed.py. */
const STRUCTURAL = [
    {
        id: "equipment-presets",
        title: "Loss-type equipment presets",
        was: "Auto-sizing only covered axial air movers, LGR dehus and air scrubbers — the wrong unit class for the carpet movers and large commercial dehus real jobs use (confirmed on Mequon + Ebenezer).",
        now: "Water / fire / mould / storm each auto-populate a realistic kit (carpet movers, large commercial dehus, extractors, scrubbers, ozone/hydroxyl, HEPA vacs, negative air). Still editable inline.",
        coeffKey: "equipment_presets",
    },
    {
        id: "crew-composition",
        title: "Loss-type crew composition templates",
        was: "A single fixed percentage split applied to every loss type and scope.",
        now: "Water, fire, mould and storm each have their own role mix. The old mix is kept as the moderate-scope fallback.",
        coeffKey: "labour_composition_templates",
    },
    {
        id: "assumption-tagging",
        title: "Production-rate assumptions are flagged",
        was: "Hardcoded production rates (sqft/crew-day, equipment ratios) looked identical to live Schedule A/B/C rates in the output.",
        now: "Every assumption-driven number carries an amber \u201cassumption \u00b7 unvalidated\u201d tag wherever it drives a figure, distinct from live rates.",
        coeffKey: "production_assumptions",
    },
    {
        id: "sub-categories",
        title: "Subcontractor categories",
        was: "Flat sub line with no structure — rental equipment booked through a sub (e.g. Ebenezer\u2019s $55,946 Sunbelt drying line) got buried in the wrong bucket.",
        now: "Section D now categorises each line: Trade Sub, Labour Sub, or Rental Equipment via Sub — so equipment costs stay visible.",
        coeffKey: null,
    },
    {
        id: "tiering",
        title: "Disposal + mobilisation tiering",
        was: "Disposal was a flat $/sqft and mobilisation a binary $600/$1,500 cliff at 100 miles — a step-function distortion on large or far jobs.",
        now: "Disposal uses declining marginal $/sqft brackets; mobilisation uses graduated distance bands plus a per-mile scale past 250 miles.",
        coeffKey: "disposal_tiers",
    },
    {
        id: "prime-toggle",
        title: "Prime-area rate switch is unmissable",
        was: "The standard/prime toggle was easy to miss — root cause of an 18% labour underprice on Ebenezer (Minneapolis billed prime, ROM priced standard).",
        now: "A bold Rate-Basis banner shows STANDARD vs PRIME up top, auto-detects prime markets (incl. Minneapolis, Chicago, Denver, DC) from the address, and warns if a prime market is detected but the toggle is off.",
        coeffKey: null,
    },
];

export default function DataGaps() {
    const navigate = useNavigate();
    const [schedule, setSchedule] = useState(null);
    const [closedCount, setClosedCount] = useState(null);

    useEffect(() => {
        (async () => {
            let sch = null;
            try {
                const { data } = await endpoints.activeRateSchedule();
                sch = data;
            } catch {
                const all = await getAll(STORES.rate_schedules);
                sch = all.find((s) => s.is_active) || all[0];
            }
            setSchedule(sch);
            try {
                const { data } = await endpoints.jobs();
                setClosedCount((data || []).filter((j) => j.actual_cost && (j.result || {}).grand_total).length);
            } catch {
                setClosedCount(null);
            }
        })();
    }, []);

    const coeffs = schedule?.coefficients || {};
    const assumptions = coeffs.production_assumptions || {};
    const resolved = (item) => !item.coeffKey || !!coeffs[item.coeffKey];
    const resolvedCount = STRUCTURAL.filter(resolved).length;

    return (
        <div className="max-w-[1100px] mx-auto px-6 py-8 space-y-8">
            <header>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold flex items-center gap-2">
                    <Wrench size={13} weight="bold" /> Model health
                </div>
                <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-1">
                    Data Gaps
                </h1>
                <p className="text-sm text-[var(--rom-text-2)] mt-2 max-w-2xl leading-relaxed">
                    Structural debt in the coefficient model, tracked down over time. Items below were
                    raised in the logic + invoice calibration review and are shipped. Production-rate
                    assumptions remain flagged as unvalidated until enough closed invoices come in to
                    calibrate them.
                </p>
            </header>

            {/* Summary tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Tile
                    icon={<CheckCircle size={20} weight="fill" className="text-[var(--rom-green)]" />}
                    label="Structural gaps resolved"
                    value={`${resolvedCount} / ${STRUCTURAL.length}`}
                    testId="gaps-resolved-tile"
                />
                <Tile
                    icon={<Flask size={20} weight="fill" className="text-amber-500" />}
                    label="Assumptions awaiting actuals"
                    value={String(Object.keys(assumptions).length)}
                    testId="gaps-assumptions-tile"
                />
                <Tile
                    icon={<ShieldCheck size={20} weight="fill" className="text-[var(--rom-navy)]" />}
                    label="Closed jobs feeding calibration"
                    value={closedCount == null ? "\u2014" : String(closedCount)}
                    testId="gaps-closed-tile"
                />
            </div>

            {/* Structural gaps */}
            <section>
                <h2 className="font-display font-bold text-lg mb-3">Structural model gaps</h2>
                <div className="space-y-3" data-testid="structural-gaps-list">
                    {STRUCTURAL.map((item) => {
                        const done = resolved(item);
                        return (
                            <div
                                key={item.id}
                                className="rom-card"
                                data-testid={`gap-${item.id}`}
                            >
                                <div className="rom-card-body">
                                    <div className="flex items-start justify-between gap-4">
                                        <h3 className="font-semibold text-base flex items-center gap-2">
                                            {item.title}
                                        </h3>
                                        <span
                                            className={`chip ${done ? "chip-green" : "chip-orange"} shrink-0`}
                                            data-testid={`gap-status-${item.id}`}
                                        >
                                            {done ? (
                                                <><CheckCircle size={12} weight="fill" className="mr-1" /> Resolved</>
                                            ) : (
                                                <><WarningCircle size={12} weight="fill" className="mr-1" /> Open</>
                                            )}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
                                        <div className="p-3 rounded-[4px] bg-slate-50 border border-[var(--rom-border)]">
                                            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-1">Was</div>
                                            <div className="text-[var(--rom-text-2)] leading-relaxed">{item.was}</div>
                                        </div>
                                        <div className="p-3 rounded-[4px] bg-[var(--rom-green-soft,#ecfdf5)] border border-emerald-200">
                                            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Now</div>
                                            <div className="text-[var(--rom-text)] leading-relaxed">{item.now}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Assumption register */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display font-bold text-lg">Assumption register (unvalidated)</h2>
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate("/calibration")}
                        data-testid="open-tuner-btn"
                    >
                        Open Coefficient Tuner <ArrowRight size={13} weight="bold" />
                    </button>
                </div>
                <div className="rom-card">
                    <table className="rom-table" data-testid="assumptions-table">
                        <thead>
                            <tr><th>Assumption</th><th>Why it's unvalidated</th><th className="numeric">Status</th></tr>
                        </thead>
                        <tbody>
                            {Object.entries(assumptions).map(([key, note]) => (
                                <tr key={key} data-testid={`assumption-${key}`}>
                                    <td className="font-medium font-mono text-xs">{key}</td>
                                    <td className="text-[var(--rom-text-2)] text-sm">{note}</td>
                                    <td className="numeric">
                                        <span className="chip chip-orange">Awaiting actuals</span>
                                    </td>
                                </tr>
                            ))}
                            {Object.keys(assumptions).length === 0 && (
                                <tr><td colSpan={3} className="text-center text-[var(--rom-text-3)] py-6">
                                    No assumption register on the active schedule yet — it seeds on next backend restart.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <p className="text-[11px] text-[var(--rom-text-3)] mt-2 leading-relaxed">
                    These drive numbers in the ROM today but haven't been checked against real invoices.
                    The calibration loop tightens them as closed jobs arrive — Tier B (full Schedule A\u2013E)
                    invoices let it pinpoint <i>which</i> coefficient to move.
                </p>
            </section>
        </div>
    );
}

function Tile({ icon, label, value, testId }) {
    return (
        <div className="rom-card" data-testid={testId}>
            <div className="rom-card-body flex items-center gap-3">
                <div className="shrink-0">{icon}</div>
                <div>
                    <div className="font-display font-black text-2xl tracking-tight">{value}</div>
                    <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{label}</div>
                </div>
            </div>
        </div>
    );
}
